import { Flex, Image, Link, Tag, TagLeftIcon, Text } from "@chakra-ui/react";
import React from "react";
import BaseImage from "@components/BaseImage";
import Tooltip from "@components/Tooltip";
import { getImageWithSubRoute } from "utils";
import UTMLink from "@components/UTMLink";

const Overview = (props: { data: any }) => {
  const { data: TEXT_STRINGS } = props;

  return (
    <Flex direction={"column"} p="6px 10px 10px 10px" gap="1.5rem">
      <Flex direction="column" gap="0.75rem">
        <Text fontSize={"16px"} fontWeight={"600"} lineHeight={"24px"}>
          {TEXT_STRINGS.MAIN.heading}
        </Text>
        <Text fontSize={"16px"} fontWeight={"400"} lineHeight={"24px"}>
          {TEXT_STRINGS.MAIN.desc}
        </Text>
      </Flex>

      {TEXT_STRINGS.ASSETS?.list?.length > 0 && (
        <Flex direction="column" gap="0.75rem">
          <Text fontSize={"16px"} fontWeight={"600"} lineHeight={"24px"}>
            {TEXT_STRINGS.ASSETS.heading}
          </Text>

          <Flex gap="6px">
            {TEXT_STRINGS.ASSETS.list.map((item: any) => (
              <Tag
                size="lg"
                color="#3F6870"
                variant="subtle"
                borderRadius="full"
                padding="0 0.25rem 0 0.25rem"
                border="0.25px solid #638E8E"
                bg="transparent"
                key={item.key}
              >
                <TagLeftIcon
                  as={Image}
                  src={getImageWithSubRoute(item.iconKey)}
                  height={{ base: "20px", md: "24px" }}
                  width={{ base: "20px", md: "24px" }}
                  alt={item.key}
                  mr="0.2rem"
                />

                {item.title}
              </Tag>
            ))}
          </Flex>
        </Flex>
      )}

      <Flex direction="column" gap="0.75rem">
        <Text fontSize={"16px"} fontWeight={"600"} lineHeight={"24px"}>
          {TEXT_STRINGS.FEES.heading}
        </Text>
        <Flex gap="24px" wrap={"wrap"}>
          {TEXT_STRINGS.FEES.data.map((item: any) => {
            return (
              <Stats
                key={item.key}
                label={item.title}
                value={item.value}
                offerValue={item.offerValue}
                {...(item.tooltip ? { tooltipText: item.tooltip } : {})}
                {...(item.link ? { link: item.link } : {})}
              />
            );
          })}
        </Flex>
      </Flex>

      <Flex direction="column" gap="0.75rem">
        <Text fontSize={"16px"} fontWeight={"600"} lineHeight={"24px"}>
          {TEXT_STRINGS.PARTNERS.heading}
        </Text>
        <Flex gap="24px" alignItems={"center"}>
          {TEXT_STRINGS.PARTNERS.details.map((p: any) => {
            return (
              <Link href={p.link} key={p.key} target="_blank" rel="noreferrer">
                <Partner
                  key={p.key}
                  imgSrc={p.imgSrc}
                  imageWidth={p.imageWidth}
                />
              </Link>
            );
          })}
        </Flex>
      </Flex>

      <Text fontSize={"14px"} fontWeight={"500"} lineHeight={"24px"}>
        By accessing Gain, you acknowledge and agree to the risks involved and
        agree to the{" "}
        <Link isExternal={true} href={TEXT_STRINGS.MAIN.disclaimerLink}>
          <u>Gain Disclaimers</u>
        </Link>
      </Text>
    </Flex>
  );
};

export default Overview;

const Stats = ({
  label,
  value,
  tooltipText,
  link,
  offerValue,
}: {
  label: string;
  value: string;
  tooltipText?: string;
  link?: string;
  offerValue?: string;
}) => {
  const extraStyle = offerValue
    ? { textDecoration: "line-through", fontSize: "24px" }
    : {};
  return (
    <Flex direction={"column"} gap="8px">
      <Flex gap="4px" alignItems="center">
        <Text fontSize={"12px"} fontWeight={"600"} lineHeight={"14.4px"}>
          {label}
        </Text>
        {tooltipText && (
          <Tooltip
            placement="top"
            label={
              <Text fontWeight="400" fontSize="12px">
                {tooltipText}
              </Text>
            }
          />
        )}
      </Flex>
      {link ? (
        <UTMLink href={link} isExternal display="flex" gap="0.5rem">
          <Text
            fontSize={"24px"}
            fontWeight={"400"}
            lineHeight={"28.8px"}
            {...extraStyle}
          >
            {value}
          </Text>
          {offerValue && (
            <Text fontSize={"24px"} fontWeight={"400"} lineHeight={"28.8px"}>
              {value}
            </Text>
          )}
          <BaseImage
            src="/assets/external-link.svg"
            width={10}
            height={5}
            alt="nexus"
          />
        </UTMLink>
      ) : (
        <Flex gap="6px">
          <Text
            fontSize={"24px"}
            fontWeight={"400"}
            lineHeight={"28.8px"}
            {...extraStyle}
          >
            {value}
          </Text>
          {offerValue && (
            <Text fontSize={"24px"} fontWeight={"400"} lineHeight={"28.8px"}>
              {offerValue}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};

const Partner = ({
  imgSrc,
  imageWidth,
}: {
  imgSrc: string;
  imageWidth?: number;
}) => {
  return (
    <BaseImage src={imgSrc} width={imageWidth || 180} height={"20"} alt="" />
  );
};
